import { useState } from 'react';
import type { JiraStatus, JiraTransition } from '@shared/types.js';

const categoryButton: Record<string, string> = {
  new: 'bg-gray-100 text-gray-800 hover:bg-gray-200',
  indeterminate: 'bg-blue-600 text-white hover:bg-blue-700',
  done: 'bg-green-600 text-white hover:bg-green-700',
  undefined: 'bg-gray-100 text-gray-800 hover:bg-gray-200',
};

const categoryDot: Record<string, string> = {
  new: 'bg-gray-400',
  indeterminate: 'bg-blue-500',
  done: 'bg-green-500',
  undefined: 'bg-gray-400',
};

interface StatusTransitionMenuProps {
  status: JiraStatus;
  transitions: JiraTransition[];
  pending: boolean;
  onTransition: (transitionId: string) => void;
}

/** Jira-style status button: shows the current status, opens a menu of transitions. */
export function StatusTransitionMenu({ status, transitions, pending, onTransition }: StatusTransitionMenuProps) {
  const [open, setOpen] = useState(false);
  const available = transitions.filter((t) => t.to.name !== status.name);

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={pending || available.length === 0}
        className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors disabled:cursor-default disabled:opacity-80 ${categoryButton[status.category] || categoryButton.undefined}`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {pending ? 'Updating...' : status.name}
        {available.length > 0 && (
          <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
      {open && (
        <>
          <button type="button" className="fixed inset-0 z-30 cursor-default" aria-label="Close status menu" onClick={() => setOpen(false)} />
          <ul role="menu" className="absolute left-0 z-40 mt-1 max-h-80 w-56 overflow-y-auto rounded-md border bg-white py-1 shadow-lg">
            {available.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { setOpen(false); onTransition(t.id); }}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                >
                  <span className={`h-2 w-2 shrink-0 rounded-full ${categoryDot[t.to.category] || categoryDot.undefined}`} />
                  <span className="flex-1 truncate">{t.name}</span>
                  {t.name.toLowerCase() !== t.to.name.toLowerCase() && (
                    <span className="truncate text-xs text-gray-400">&rarr; {t.to.name}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
